import { lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react'
import { Boxes } from 'lucide-react'
import type { Module, ModuleInstance } from '@syscolect/domain'

/**
 * App-level view of a module for the shell (sidebar + workspace cards). Extends
 * the domain `Module` with a `slug` (the `/m/:slug` key) and `label`/`iconNode`
 * for display. `iconNode` is named to avoid clashing with `Module.icon` (a
 * string icon name in the domain).
 */
export interface AppModule extends Module {
  slug: string
  label: string
  iconNode: ReactNode
}

/**
 * Last-resort icon for a module whose catalog record carries no `icon` token.
 *
 * There used to be a `slugToIcon` map here with one hand-written lucide element
 * per slug. It was dead weight the moment the catalog record became the source
 * of truth — `ModuleResolverBridge` renders `<Icon name={record.icon}>` and only
 * fell through to the map when the token was missing (request 0195).
 */
export const defaultIcon: ReactNode = <Boxes />

/**
 * slug → page component, derived from the filesystem instead of a hand-written
 * list.
 *
 * The convention: one `<Name>Page.tsx` per directory under `src/modules/`, and
 * the slug is the directory name without its dashes (`stock-location` →
 * `stocklocation`). Vite compiles every glob entry into its own dynamic import,
 * so each module lands in its own chunk — and, crucially, there is no longer a
 * place where a static `import { XPage }` could quietly merge all of them back
 * into the entry chunk, which is what used to happen (request 0194).
 *
 * `moduleRegistry.test.ts` guards the convention: one page per directory, no
 * two directories colliding once the dashes are dropped, and an exported
 * component named after the file.
 *
 * A slug with no page here falls back to `GenericModulePage` in `ModuleEntry`.
 */
const pageLoaders = import.meta.glob('/src/modules/*/*Page.tsx')

/** `/src/modules/stock-location/StockLocationPage.tsx` → `stocklocation`. */
function slugFromPath(path: string): string {
  return path.split('/').at(-2)!.replace(/-/g, '')
}

/** `/src/modules/stock-location/StockLocationPage.tsx` → `StockLocationPage`. */
function exportNameFromPath(path: string): string {
  return path.split('/').at(-1)!.replace(/\.tsx$/, '')
}

export type ModulePageComponent = LazyExoticComponent<
  ComponentType<{ instance: ModuleInstance }>
>

export const slugToComponent: Record<string, ModulePageComponent> = Object.fromEntries(
  Object.entries(pageLoaders).map(([path, load]) => [
    slugFromPath(path),
    lazy(async () => {
      const mod = (await load()) as Record<
        string,
        ComponentType<{ instance: ModuleInstance }>
      >
      return { default: mod[exportNameFromPath(path)] }
    }),
  ]),
)
